/**
 * Task 5.4: Model Report - Prediction accuracy and recommendation value
 * Shows predicted vs actual, MAE/RMSE, accuracy trends, attribution by recommendations applied.
 */
import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  TrendingUp,
  Assessment,
  ShowChart,
  Lightbulb,
  Timeline,
} from '@mui/icons-material';
import { mlAnalysisAPI } from '../services/api';
import PageLoadingState from '../components/PageLoadingState';
import { DataTable, MetricCard, PageHeader } from '../components/primitives';

export default function ModelReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(90);

  useEffect(() => {
    loadReport();
  }, [days]);

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await mlAnalysisAPI.getModelReport(days);
      setReport(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load model report');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !report) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <PageLoadingState minHeight={320} message="Loading model report…" />
      </Container>
    );
  }

  const overall = report?.predicted_vs_actual?.overall || {};
  const byPlatform = report?.accuracy_summary?.by_platform || {};
  const byContentType = report?.accuracy_summary?.by_content_type || {};
  const trends = report?.accuracy_trends || [];
  const attribution = report?.attribution || {};
  const adoption = report?.recommendation_adoption || {};

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader
        title="Model Performance Report"
        subtitle="Prediction accuracy and the measurable value of applying recommendations"
        icon={<Assessment sx={{ fontSize: 28 }} />}
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Time range</InputLabel>
              <Select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                label="Time range"
              >
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={90}>Last 90 days</MenuItem>
                <MenuItem value={180}>Last 180 days</MenuItem>
              </Select>
            </FormControl>
            {report?.evaluated_at && (
              <Typography variant="caption" color="text.secondary">
                Evaluated: {new Date(report.evaluated_at).toLocaleString()}
              </Typography>
            )}
          </Box>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {report && overall?.n === 0 && !error && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No prediction data available yet. Run backfill for competitor posts with engagement snapshots to populate predicted vs actual metrics.
        </Alert>
      )}

      {/* Accuracy Summary */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Overall MAE"
            value={overall?.mae != null ? overall.mae.toFixed(2) : '—'}
            subtitle={overall?.n != null ? `${overall.n} predictions` : 'No data'}
            icon={<TrendingUp />}
            accentColor="#58a6ff"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Overall RMSE"
            value={overall?.rmse != null ? overall.rmse.toFixed(2) : '—'}
            subtitle="Lower is better"
            icon={<ShowChart />}
            accentColor="#58a6ff"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            variant="compact"
            title="Recommendation Adoption"
            value={
              report?.recommendation_adoption_rate != null
                ? `${(report.recommendation_adoption_rate * 100).toFixed(1)}%`
                : '—'
            }
            subtitle={
              report?.recommendation_adoption_rate != null
                ? `of published posts used recommendations${
                    adoption?.total_recommendations_applied != null
                      ? ` · ${adoption.total_recommendations_applied} applied`
                      : ''
                  }`
                : attribution?.message ||
                  (attribution?.segments?.length > 0
                    ? 'See attribution by segment below'
                    : 'Data will appear as creators apply suggestions')
            }
            icon={<Lightbulb />}
            accentColor="#d29922"
          />
        </Grid>
      </Grid>

      {/* By Platform & Content Type */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Accuracy by Platform
              </Typography>
              <DataTable
                empty={Object.keys(byPlatform).length === 0}
                emptyMessage="No platform breakdown"
                component={Box}
                minHeight={120}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Platform</TableCell>
                    <TableCell align="right">MAE</TableCell>
                    <TableCell align="right">RMSE</TableCell>
                    <TableCell align="right">n</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(byPlatform).map(([plat, m]) => (
                    <TableRow key={plat}>
                      <TableCell>{plat}</TableCell>
                      <TableCell align="right">
                        {m?.mae != null ? m.mae.toFixed(2) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {m?.rmse != null ? m.rmse.toFixed(2) : '—'}
                      </TableCell>
                      <TableCell align="right">{m?.n ?? 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </DataTable>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Accuracy by Content Type
              </Typography>
              <DataTable
                empty={Object.keys(byContentType).length === 0}
                emptyMessage="No content type breakdown"
                component={Box}
                minHeight={120}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Content Type</TableCell>
                    <TableCell align="right">MAE</TableCell>
                    <TableCell align="right">RMSE</TableCell>
                    <TableCell align="right">n</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(byContentType).map(([ct, m]) => (
                    <TableRow key={ct}>
                      <TableCell>{ct}</TableCell>
                      <TableCell align="right">
                        {m?.mae != null ? m.mae.toFixed(2) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {m?.rmse != null ? m.rmse.toFixed(2) : '—'}
                      </TableCell>
                      <TableCell align="right">{m?.n ?? 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </DataTable>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Accuracy Trends */}
      {trends.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Timeline color="primary" />
              <Typography variant="h6">
                Prediction Accuracy Trends
              </Typography>
            </Box>
            <DataTable empty={false} component={Box} minHeight={120}>
              <TableHead>
                <TableRow>
                  <TableCell>Week</TableCell>
                  <TableCell align="right">MAE</TableCell>
                  <TableCell align="right">RMSE</TableCell>
                  <TableCell align="right">n</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trends.map((t) => (
                  <TableRow key={t.period}>
                    <TableCell>{t.period}</TableCell>
                    <TableCell align="right">{t.mae?.toFixed(2) ?? '—'}</TableCell>
                    <TableCell align="right">{t.rmse?.toFixed(2) ?? '—'}</TableCell>
                    <TableCell align="right">{t.n ?? 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>
          </CardContent>
        </Card>
      )}

      {attribution?.segments?.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Attribution by Recommendations Applied
            </Typography>
            <Grid container spacing={2}>
              {attribution.segments.map((seg) => (
                <Grid item xs={12} md={4} key={seg.segment}>
                  <MetricCard
                    variant="centered"
                    title={seg.segment}
                    value={
                      seg.avg_actual_engagement != null
                        ? seg.avg_actual_engagement.toFixed(1)
                        : '—'
                    }
                    subtitle={`avg engagement · n=${seg.n}`}
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Container>
  );
}
